<script setup lang="ts">
import { type CSSProperties, ref } from 'vue'

interface TooltipApplierProps {
  label: string
  align?: 'center' | 'left' | 'right' // top/bottom
  isRed?: boolean
  position?: 'bottom' | 'left' | 'right' | 'top'
  // Render the tooltip into <body> via fixed positioning. Use this when the
  // trigger lives inside an `overflow: hidden` container (e.g. the sidebar),
  // where a normally positioned tooltip would be clipped.
  teleport?: boolean
  vAlign?: 'bottom' | 'center' | 'top' // left/right
}

const {
  align = 'center',
  isRed = false,
  label,
  position = 'bottom',
  teleport = false,
  vAlign = 'center',
} = defineProps<TooltipApplierProps>()

defineSlots<{
  default(): unknown
}>()

const GAP = 8

const triggerRef = ref<HTMLElement | null>(null)
const isHovered = ref(false)
const teleportStyle = ref<CSSProperties>({})

const updateTeleportPosition = (): void => {
  const element = triggerRef.value
  if (!element) return

  const rect = element.getBoundingClientRect()

  switch (position) {
    case 'top':
      teleportStyle.value = {
        bottom: `${window.innerHeight - rect.top + GAP}px`,
        left: `${rect.left + rect.width / 2}px`,
        position: 'fixed',
        transform: 'translateX(-50%)',
      }
      break
    case 'bottom':
      teleportStyle.value = {
        left: `${rect.left + rect.width / 2}px`,
        position: 'fixed',
        top: `${rect.bottom + GAP}px`,
        transform: 'translateX(-50%)',
      }
      break
    case 'left':
      teleportStyle.value = {
        position: 'fixed',
        right: `${window.innerWidth - rect.left + GAP}px`,
        top: `${rect.top + rect.height / 2}px`,
        transform: 'translateY(-50%)',
      }
      break
    case 'right':
      teleportStyle.value = {
        left: `${rect.right + GAP}px`,
        position: 'fixed',
        top: `${rect.top + rect.height / 2}px`,
        transform: 'translateY(-50%)',
      }
      break
    default:
      break
  }
}

const onPointerEnter = (): void => {
  if (!teleport) return
  updateTeleportPosition()
  isHovered.value = true
}

const onPointerLeave = (): void => {
  isHovered.value = false
}
</script>

<template>
  <div
    ref="triggerRef"
    class="group relative inline-block"
    @pointerenter="onPointerEnter"
    @pointerleave="onPointerLeave"
  >
    <Teleport v-if="teleport" to="body">
      <div
        v-if="label && isHovered"
        class="pointer-events-none fixed z-50 whitespace-nowrap rounded-lg p-2.5 text-xs font-medium shadow-md"
        :class="[isRed ? 'bg-radical_red-50 text-radical_red-500' : 'text-black bg-black-50']"
        :style="teleportStyle"
      >
        {{ label }}
      </div>
    </Teleport>

    <div
      v-else-if="label"
      class="absolute z-10 hidden whitespace-nowrap rounded-lg p-2.5 text-xs font-medium shadow-md group-hover:block"
      :class="[
        // Color-Theme
        isRed ? 'bg-radical_red-50 text-radical_red-500' : 'text-black bg-black-50',

        // Positionierung
        position === 'top' && 'bottom-full mb-2',
        position === 'bottom' && 'top-full mt-2',
        position === 'left' && 'right-full mr-2',
        position === 'right' && 'left-full ml-2',

        // Alignment for top/bottom
        position === 'top' || position === 'bottom'
          ? align === 'center'
            ? 'left-1/2 -translate-x-1/2'
            : align === 'left'
              ? 'left-0'
              : 'right-0'
          : '',

        // Alignment for left/right
        position === 'left' || position === 'right'
          ? vAlign === 'center'
            ? 'top-1/2 -translate-y-1/2'
            : vAlign === 'top'
              ? 'top-0 translate-y-0'
              : 'bottom-0 translate-y-0'
          : '',
      ]"
    >
      {{ label }}
    </div>

    <slot></slot>
  </div>
</template>
